import { useState, useCallback, useEffect, useLayoutEffect } from 'react';

const KEYBOARD_OPEN_THRESHOLD_PX = 48;

/**
 * Positions a fixed bottom sheet within the visual viewport (above the virtual keyboard).
 * Returns { style, syncViewport } — call syncViewport on search focus for immediate layout.
 */
export function useVisualViewportSheetStyle({ enabled = true, topMargin = 8 } = {}) {
  const [style, setStyle] = useState(null);

  const syncViewport = useCallback(() => {
    if (!enabled || typeof window === 'undefined') {
      setStyle(null);
      return;
    }

    const vv = window.visualViewport;
    if (!vv) {
      setStyle({ bottom: 0, top: 'auto', maxHeight: 'min(85vh, 520px)' });
      return;
    }

    const keyboardInset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    const keyboardOpen = keyboardInset > KEYBOARD_OPEN_THRESHOLD_PX;
    const maxHeight = Math.max(160, vv.height - topMargin);

    if (keyboardOpen) {
      // Pin to the visible viewport while the keyboard is open
      setStyle({
        top: `${vv.offsetTop}px`,
        bottom: 'auto',
        maxHeight: `${maxHeight}px`,
      });
    } else {
      setStyle({
        top: 'auto',
        bottom: 0,
        maxHeight: `${Math.min(maxHeight, 520)}px`,
      });
    }
  }, [enabled, topMargin]);

  useLayoutEffect(() => {
    syncViewport();
  }, [syncViewport]);

  useEffect(() => {
    if (!enabled) return;

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(syncViewport);
    };

    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('scroll', schedule);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('scroll', schedule);
    };
  }, [enabled, syncViewport]);

  return { style, syncViewport };
}

export default useVisualViewportSheetStyle;
